/**
 * EditAccountDrawer — edit a key-based RudderStack account's name and options, and rotate one of its
 * secrets, without leaving Lookout (spec §7). Mirrors `AddRudderstackAccountDrawer`'s shape: a
 * `WizardHeader` naming the screen, `AccountForm` (edit mode) in a scroll box, and Save in the
 * shared `WizardFooter` below it.
 *
 * OAuth accounts never reach this drawer — spec §7/Task 15 gates Edit to non-OAuth accounts and
 * routes OAuth accounts through `ReauthorizeAction` instead (the update RPC also refuses OAuth
 * accounts server-side, so this is belt-and-suspenders, not the only guard).
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Callout } from '@/_shared/components/ui/callout';
import { Sheet, SheetContent } from '@/_shared/components/ui/sheet';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { WizardFooter } from '@/_shared/components/wizard/WizardFooter';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useRudderstackSignInAgain } from '@/_shared/hooks/useRudderstackSignInAgain';
import { isOrpcConflictError, orpcErrorMessage } from '@/_shared/lib/orpc-errors';

import { WizardHeader } from '../warehouseAccount/addAccount/WizardHeader';
import { AccountForm, type AccountFormHandle } from './AccountForm';
import { DestinationBadge } from './DestinationBadge';

type AccountView = Awaited<ReturnType<RouterClient<AppRouter>['settings']['rudderstackAccounts']['get']>>['account'];

interface EditAccountDrawerProps {
  accountId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  surface: 'activation' | 'settings';
  onSaved?: (account: { accountId: string; name: string }) => void;
}

/** What the body reports up for its host to render — the same contract `AddAccountChrome` has. */
export interface EditAccountChrome {
  primary: {
    label: string;
    disabled: boolean;
    loading: boolean;
    onClick: () => void;
    /** Why Save is dead, for a definition Lookout cannot render a form for. */
    disabledReason?: string | null;
  } | null;
  /** Nothing here opens a popup, so this is always true — kept so both bodies report one shape. */
  canLeave: boolean;
}

export function EditAccountDrawer({
  accountId,
  open,
  onOpenChange,
  surface,
  onSaved,
}: EditAccountDrawerProps): React.JSX.Element {
  const orpc = useOrpc();
  const [chrome, setChrome] = useState<EditAccountChrome | null>(null);
  /**
   * The drawer reads the account for its BADGE only.
   *
   * A second `useQuery` on the key the body already holds, which costs no second request: TanStack
   * dedupes by query key, so this subscribes to the same cache entry rather than fetching again. The
   * alternative was reporting the badge up through the chrome — widening a contract that exists for
   * ACTIONS with a piece of presentation — or leaving the header inside the body, which is what
   * forced the `hosted` branch this change removes.
   */
  const account = useQuery(orpc.settings.rudderstackAccounts.get.queryOptions({ input: { accountId } })).data?.account;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" showCloseButton={false} className="w-full gap-0 sm:max-w-drawer">
        <WizardHeader
          title="Edit account"
          badge={
            account ? (
              <DestinationBadge destinationType={account.destinationType} label={account.destinationDisplayName} />
            ) : null
          }
        />
        {/* The scroll box belongs to the HOST, for the reason `AddRudderstackAccountScreen` states:
            the activation wizard has one of its own, and a body that brought its own nested two
            scrollers inside it — which is exactly what this body used to do. */}
        <div className="flex-1 overflow-y-auto p-4">
          <EditAccountBody
            accountId={accountId}
            onOpenChange={onOpenChange}
            surface={surface}
            onSaved={onSaved}
            onChromeChange={setChrome}
          />
        </div>
        {/* Save moved out of the header, where it sat beside the title, the badge and the close. No
            Back: editing is one screen, so there is nowhere behind it to go — the close is the way
            out, and `WizardFooter` draws only what it is given. */}
        <WizardFooter primary={chrome?.primary ?? null} />
      </SheetContent>
    </Sheet>
  );
}

/**
 * The edit form WITHOUT a shell, so two hosts can render it: the drawer above (Settings), and the
 * activation wizard, where editing an account is a step rather than a sheet stacked on the sheet.
 *
 * It draws no header, no footer and no scroll box — only the form and the states around it — and
 * reports its Save button up through `onChromeChange`, which every host must supply. That was
 * briefly optional, with an absent handler meaning "draw your own header and Save inside it". Once
 * Settings also rendered the shared footer there was no second behaviour left for the flag to
 * select, and the branch went with it.
 */
export function EditAccountBody({
  accountId,
  onOpenChange,
  surface,
  onSaved,
  onChromeChange,
}: Omit<EditAccountDrawerProps, 'open'> & {
  onChromeChange: (chrome: EditAccountChrome) => void;
}): React.JSX.Element {
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const formRef = useRef<AccountFormHandle>(null);
  const [formError, setFormError] = useState<string | null>(null);
  // ACT2-1124: an ended RudderStack sign-in offers "Sign in again" on the toast.
  const signIn = useRudderstackSignInAgain();

  const accountQuery = useQuery(orpc.settings.rudderstackAccounts.get.queryOptions({ input: { accountId } }));
  const account: AccountView | undefined = accountQuery.data?.account;

  // The definition's form fields aren't on the account read — fetch the one definition this
  // account belongs to, once the account itself has resolved it.
  const definitionsQuery = useQuery({
    ...orpc.settings.rudderstackAccounts.listDefinitions.queryOptions({
      input: account ? { names: [account.accountDefinitionName] } : {},
    }),
    enabled: !!account,
  });
  const definition = definitionsQuery.data?.definitions.find((d) => d.name === account?.accountDefinitionName);

  // A key-based definition with no form fields (SOURCE_POSTGRES, SOURCE_BIGQUERY, SOURCE_SNOWFLAKE
  // ship no ui-config) resolves `lookoutSupport: 'external'` — `AccountForm` would render nothing to
  // fill in, so Save must not stay live over an empty form. A definitions-fetch failure lands in the
  // same "nothing to render" shape and gets the same treatment.
  const definitionsFailed = definitionsQuery.isError;
  const formSupported = !!definition && definition.lookoutSupport === 'form';

  /* eslint-disable react-hooks/refs -- the update-mutation's onError reads formRef only when the async callback runs, never during render */
  const updateMutation = useMutation(
    orpc.settings.rudderstackAccounts.update.mutationOptions({
      onSuccess: (_data, variables) => {
        const name = variables.name ?? account?.name ?? '';
        toast.success(`Saved ${name}`);
        onSaved?.({ accountId, name });
        // All account-backed query keys, always (shared UI notes §11): settings, activation picker,
        // and audience detail bindings read the same accounts, so invalidating only the settings key
        // would leave a stale name/secret state visible there.
        void queryClient.invalidateQueries({ queryKey: orpc.settings.rudderstackAccounts.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.audience.listActivationAccounts.key() });
        void queryClient.invalidateQueries({ queryKey: orpc.audience.listRudderstackWarehouseAccounts.key() });
        // REMOVE (not invalidate) this account's own `get` cache entry. Invalidate only marks it
        // stale and schedules a background refetch; it does not clear the cached value, and TanStack
        // Query v5's `isLoading` is false the instant ANY cached data exists for a key, stale or not.
        // A reopen would then render `AccountForm` immediately against the pre-rename snapshot — and
        // `AccountForm`'s `defaultValues` memo is keyed on `[fields, account?.id]`, which a rename
        // never changes, so it would stick even once the fresh data lands a moment later. Save again
        // for any reason (e.g. rotating a secret) and the stale name gets sent back and accepted,
        // silently reverting a rename the user already saw confirmed. Removing here means a reopen has
        // genuinely no cached data, shows its skeleton, and renders only once fresh data arrives —
        // same fix shape as `AudienceActivationSheet`'s `readActivationConfig` eviction, and scoped to
        // this one account (not the whole procedure) since sibling accounts' caches are untouched.
        queryClient.removeQueries({ queryKey: orpc.settings.rudderstackAccounts.get.key({ input: { accountId } }) });
        onOpenChange(false);
      },
      onError: (err) => {
        // A rename to an already-used name comes back as CONFLICT — attach it to the name field via
        // the form handle (same pattern as AddRudderstackAccountDrawer) so the user sees which field
        // to fix, instead of a generic banner that says the name is taken but not where.
        if (isOrpcConflictError(err)) {
          // `orpcErrorMessage`, not `err.message`: the platform's own text now travels under
          // `data.message` because the top-level message is logged unredacted (see its doc comment).
          formRef.current?.setError('name', orpcErrorMessage(err, 'This name is already in use'));
          return;
        }
        if (signIn.toastIfReauth(err)) return;
        setFormError(orpcErrorMessage(err, 'Failed to save account'));
      },
    }),
  );
  /* eslint-enable react-hooks/refs -- end update-mutation block */

  const handleSave = async (): Promise<void> => {
    if (!formRef.current) return;
    const valid = await formRef.current.validate();
    if (!valid) return;
    setFormError(null);
    const values = formRef.current.getValues();
    try {
      await updateMutation.mutateAsync({ accountId, name: values.name, options: values.options, surface });
    } catch {
      // onError above already updated the UI; this guard just keeps mutateAsync's rejection from
      // surfacing as an unhandled promise rejection.
    }
  };

  const isLoading = accountQuery.isLoading || (!!account && definitionsQuery.isLoading);
  // True only once everything has resolved and there is genuinely nothing to submit — not while
  // still loading, and not merely because the account read itself failed (that already disables
  // Save on its own via `accountQuery.isError` below).
  const isFormless = !isLoading && !accountQuery.isError && !definitionsFailed && !!account && !formSupported;
  const saveDisabled = updateMutation.isPending || isLoading || accountQuery.isError || definitionsFailed || isFormless;

  // Report the primary to the host, and keep it fresh as the form settles. `onClick` is read from
  // the latest render rather than captured once, for the same reason `AddAccountChrome` says: a host
  // may hold this object in state across renders.
  useEffect(() => {
    onChromeChange({
      primary: {
        label: 'Save',
        disabled: saveDisabled,
        loading: updateMutation.isPending,
        onClick: () => void handleSave(),
        // Only for the formless case. Save is also disabled while the account loads and after a
        // failed read, and both of those the screen already explains — a tooltip repeating a
        // skeleton or an error callout beside it is noise.
        disabledReason: isFormless ? 'This account type is managed in RudderStack, not edited here' : null,
      },
      canLeave: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `handleSave` is recreated every render by design; the chrome is refreshed on the values that matter
  }, [saveDisabled, isFormless, updateMutation.isPending]);

  return (
    <>
      {accountQuery.isError && <Callout variant="error">Could not load this account.</Callout>}
      {!accountQuery.isError && definitionsFailed && (
        <Callout variant="error">Could not load this account type.</Callout>
      )}

      {isLoading && (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {formError && <Callout variant="error">{formError}</Callout>}

      {!isLoading && account && formSupported && (
        <AccountForm
          // Keyed on accountId so a background refetch of `get` (same id, fresh object identity)
          // never remounts the form under the user's cursor.
          key={accountId}
          ref={formRef}
          fields={definition?.form.fields ?? []}
          // The platform's own secret split — what the write path may omit when blank. Not the
          // ui-config `secret` flag, which only drives masking (see AccountForm).
          secretFields={definition?.secretFields ?? []}
          definitionName={account.accountDefinitionName}
          account={{
            id: account.id,
            name: account.name,
            options: account.options,
            secretKeys: account.secretKeys,
          }}
        />
      )}

      {isFormless && (
        <Callout variant="info">
          {account?.destinationDisplayName ?? 'This account'} accounts are managed in RudderStack — this account type
          can&apos;t be edited here.
        </Callout>
      )}
    </>
  );
}
